import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  ReactFlow,
  Background,
  Controls,
  MarkerType,
  Handle,
  Position,
  useNodesState,
  useEdgesState,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import "./EsmFlowchart.scss";
import logoEsm from "../../../images/Logo/logoESM.png";

// Custom Node Components
const BlueBoxNode = ({ data }) => (
  <div className="flow-node flow-blue-box">
    <Handle type="target" position={Position.Top} style={{ visibility: 'hidden' }} />
    <span>{data.label}</span>
    <Handle type="source" position={Position.Bottom} style={{ visibility: 'hidden' }} />
    <Handle type="source" position={Position.Right} id="right" style={{ visibility: 'hidden' }} />
  </div>
);

const GreenBoxNode = ({ data }) => (
  <div className="flow-node flow-green-box">
    <Handle type="target" position={Position.Left} style={{ visibility: 'hidden' }} />
    <span dangerouslySetInnerHTML={{ __html: data.label }} />
    <Handle type="source" position={Position.Right} id="right" style={{ visibility: 'hidden' }} />
    <Handle type="source" position={Position.Bottom} id="bottom" style={{ visibility: 'hidden' }} />
    <Handle type="source" position={Position.Top} id="top" style={{ visibility: 'hidden' }} />
  </div>
);

const GreenCircleNode = ({ data }) => (
  <div className="flow-node flow-green-circle">
    <Handle type="target" position={Position.Top} style={{ visibility: 'hidden' }} />
    <Handle type="target" position={Position.Left} id="left" style={{ visibility: 'hidden' }} />
    <span dangerouslySetInnerHTML={{ __html: data.label }} />
    <Handle type="source" position={Position.Bottom} style={{ visibility: 'hidden' }} />
    <Handle type="source" position={Position.Left} id="source-left" style={{ visibility: 'hidden' }} />
  </div>
);

const LightCircleNode = ({ data }) => (
  <div className="flow-node flow-light-circle">
    <Handle type="target" position={Position.Bottom} style={{ visibility: 'hidden' }} />
    <span dangerouslySetInnerHTML={{ __html: data.label }} />
    <Handle type="source" position={Position.Top} style={{ visibility: 'hidden' }} />
    <Handle type="source" position={Position.Right} id="right" style={{ visibility: 'hidden' }} />
  </div>
);

const HeaderBoxNode = ({ data }) => (
  <div className="flow-node flow-header-box">
    <Handle type="target" position={Position.Bottom} style={{ visibility: 'hidden' }} />
    <Handle type="target" position={Position.Right} id="right" style={{ visibility: 'hidden' }} />
    <span>{data.label}</span>
  </div>
);

const LogoBoxNode = ({ data }) => (
  <div className="flow-node flow-logo-box">
    <Handle type="target" position={Position.Right} style={{ visibility: 'hidden' }} />
    <Handle type="target" position={Position.Bottom} id="target-bottom" style={{ visibility: 'hidden' }} />
    <img src={logoEsm} alt="ESM Logo" />
    <Handle type="source" position={Position.Top} id="top" style={{ visibility: 'hidden' }} />
    <Handle type="source" position={Position.Bottom} id="bottom" style={{ visibility: 'hidden' }} />
    <Handle type="source" position={Position.Right} id="right" style={{ visibility: 'hidden' }} />
  </div>
);

const DashedBoxNode = ({ data }) => (
  <div className="flow-node flow-dashed-box" style={{ width: data.width, height: data.height }}>
    <Handle type="target" position={Position.Left} style={{ visibility: 'hidden' }} />
    <div className="dashed-title">{data.label}</div>
  </div>
);

const SimpleTextNode = ({ data }) => (
  <div className={`flow-node flow-simple-text ${data.dashed ? 'dashed' : ''}`}>
    <Handle type="target" position={Position.Left} style={{ visibility: 'hidden' }} />
    <Handle type="target" position={Position.Right} id="right" style={{ visibility: 'hidden' }} />
    <span dangerouslySetInnerHTML={{ __html: data.label }} />
    <Handle type="source" position={Position.Top} style={{ visibility: 'hidden' }} />
    <Handle type="source" position={Position.Left} id="left" style={{ visibility: 'hidden' }} />
  </div>
);

// Define Initial Nodes
const getInitialNodes = (t) => [
  // Background Groups
  {
    id: "group-y-te",
    type: "dashedBox",
    position: { x: 30, y: 70 },
    data: { 
      label: t("esm_flowchart.khoi_y_te"), 
      width: 300, 
      height: 480 
    },
    style: { zIndex: -1 },
    draggable: false,
  },
  
  // Left Medical Block
  { id: "tai-chinh", type: "blueBox", position: { x: 100, y: 150 }, data: { label: t("esm_flowchart.tai_chinh") }, draggable: false },
  { id: "cskh", type: "blueBox", position: { x: 100, y: 250 }, data: { label: t("esm_flowchart.cskh") }, draggable: false },
  { id: "nhan-su", type: "blueBox", position: { x: 100, y: 360 }, data: { label: t("esm_flowchart.nhan_su") }, draggable: false },
  { id: "van-hanh", type: "blueBox", position: { x: 100, y: 460 }, data: { label: t("esm_flowchart.van_hanh") }, draggable: false },

  // Far Left Report Circle
  { id: "bao-cao", type: "lightCircle", position: { x: -80, y: 450 }, data: { label: t("esm_flowchart.bao_cao") }, draggable: false },

  // Bottom Left ESM Logo
  { id: "logo-esm", type: "logoBox", position: { x: 100, y: 650 }, data: { label: "ESM" }, draggable: false },

  // Top Header Service Block
  { id: "khach-hang", type: "headerBox", position: { x: 350, y: -40 }, data: { label: t("esm_flowchart.khach_hang_title") }, draggable: false },

  // Right Service Nodes - Main Column
  { id: "dich-vu", type: "greenBox", position: { x: 450, y: 150 }, data: { label: t("esm_flowchart.dich_vu") }, draggable: false },
  { id: "goi-kham", type: "greenBox", position: { x: 450, y: 250 }, data: { label: t("esm_flowchart.goi_kham") }, draggable: false },
  { id: "cham-soc", type: "greenBox", position: { x: 450, y: 350 }, data: { label: t("esm_flowchart.cham_soc") }, draggable: false },
  { id: "duoc-pham", type: "greenBox", position: { x: 450, y: 450 }, data: { label: t("esm_flowchart.duoc_pham") }, draggable: false },

  // Top Right Sub-services (Stacked vertically to form a perfect fork with Dich vu)
  { id: "mua-bao-hiem", type: "greenBox", position: { x: 650, y: 50 }, data: { label: t("esm_flowchart.mua_bh") }, draggable: false },
  { id: "tuyen-dung", type: "greenBox", position: { x: 650, y: 150 }, data: { label: t("esm_flowchart.tuyen_dung") }, draggable: false },

  // Bottom Center/Right Nodes
  { id: "thanh-toan", type: "greenCircle", position: { x: 850, y: 400 }, data: { label: t("esm_flowchart.thanh_toan") }, draggable: false },
  { id: "ngan-hang", type: "greenCircle", position: { x: 850, y: 600 }, data: { label: t("esm_flowchart.ngan_hang") }, draggable: false },
  
  { id: "the-ngan-hang", type: "simpleText", position: { x: 1000, y: 600 }, data: { label: t("esm_flowchart.the_ngan_hang"), dashed: true }, draggable: false },
  { id: "zalo-sms", type: "simpleText", position: { x: 1050, y: 780 }, data: { label: t("esm_flowchart.zalo_sms"), dashed: false }, draggable: false },
];

// Helper to create edge
const createEdge = (id, source, target, animated = false, dashed = false, type = "default", targetHandle = null, sourceHandle = null) => {
  const edge = {
    id,
    source,
    target,
    animated,
    type,
    style: {
      stroke: "#3b86c4",
      strokeWidth: 1.5,
      strokeDasharray: dashed ? "5 5" : "none",
    },
    markerEnd: {
      type: MarkerType.ArrowClosed,
      color: "#3b86c4",
    },
  };
  if (targetHandle) edge.targetHandle = targetHandle;
  if (sourceHandle) edge.sourceHandle = sourceHandle;
  return edge;
};

const initialEdges = [
  // Medical block internal connections (Straight lines)
  createEdge("e-tai-cskh", "tai-chinh", "cskh", false, false, "straight"),
  createEdge("e-cskh-nhan", "cskh", "nhan-su", false, false, "straight"),
  createEdge("e-nhan-van", "nhan-su", "van-hanh", false, false, "straight"),

  // From CSKH to Services (Straight diagonals fan out perfectly without intersecting boxes below them)
  createEdge("e-cskh-dv", "cskh", "dich-vu", true, true, "straight", null, "right"),
  createEdge("e-cskh-goi", "cskh", "goi-kham", true, true, "straight", null, "right"),
  createEdge("e-cskh-cham", "cskh", "cham-soc", true, true, "straight", null, "right"),
  createEdge("e-cskh-duoc", "cskh", "duoc-pham", true, true, "straight", null, "right"),

  // From Dịch Vụ to Mua BH and Tuyển dụng (Perfect fork shape)
  createEdge("e-dv-bh", "dich-vu", "mua-bao-hiem", true, true, "smoothstep"),
  createEdge("e-dv-tuyen", "dich-vu", "tuyen-dung", true, true, "straight"), // Horizontal alignment
  
  // From Services to Payment (Bezier curves converge gracefully to the Left handle of Thanh Toán)
  createEdge("e-dv-tt", "dich-vu", "thanh-toan", true, false, "default", "left"),
  createEdge("e-goi-tt", "goi-kham", "thanh-toan", true, false, "default", "left"),
  createEdge("e-cham-tt", "cham-soc", "thanh-toan", true, false, "default", "left"),
  createEdge("e-duoc-tt", "duoc-pham", "thanh-toan", true, false, "default", "left"),

  // Payment to Bank
  createEdge("e-tt-nh", "thanh-toan", "ngan-hang", true, false, "straight"),
  
  // Bank pointing TO ESM
  createEdge("e-nh-esm", "ngan-hang", "logo-esm", true, false, "default"),

  // ESM pointing to Zalo (Out from Bottom handle to avoid crossing)
  createEdge("e-esm-zalo", "logo-esm", "zalo-sms", true, true, "smoothstep", null, "bottom"),

  // ESM to Bao Cao
  createEdge("e-esm-bao", "logo-esm", "bao-cao", true, true, "smoothstep"),

  // Zalo to Top Header
  createEdge("e-zalo-top", "zalo-sms", "khach-hang", true, true, "smoothstep", "right"),

  // Bao Cao to Medical Block
  createEdge("e-bao-yte", "bao-cao", "group-y-te", true, true, "default"),
];

const EsmFlowchart = () => {
  const { t } = useTranslation();
  
  const translatedInitialNodes = useMemo(() => getInitialNodes(t), [t]);

  const [nodes, setNodes, onNodesChange] = useNodesState(translatedInitialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Update nodes when translation changes
  React.useEffect(() => {
    setNodes(translatedInitialNodes);
  }, [translatedInitialNodes, setNodes]);

  const nodeTypes = useMemo(
    () => ({
      blueBox: BlueBoxNode,
      greenBox: GreenBoxNode,
      greenCircle: GreenCircleNode,
      lightCircle: LightCircleNode,
      headerBox: HeaderBoxNode,
      logoBox: LogoBoxNode,
      dashedBox: DashedBoxNode,
      simpleText: SimpleTextNode,
    }),
    []
  );

  return (
    <div className="ep-flowchart-container">
      <h3 className="ep-flowchart-title">{t("esm_flowchart.title")}</h3>
      <div className="flow-canvas-wrapper">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.1 }}
          minZoom={0.5}
          maxZoom={2}
          attributionPosition="bottom-right"
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
        >
          <Background color="#cbd5e0" gap={20} size={1} />
          <Controls showInteractive={false} />
        </ReactFlow>
      </div>
    </div>
  );
};

export default EsmFlowchart;
